import { useEffect, useState, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchPatients } from '../store/patientListSlice'
import { PatientListTable } from '../components/patients/PatientListTable'
import { PatientSearch } from '../components/patients/PatientSearch'
import { AddPatientModal } from '../components/patients/AddPatientModal'
import { Plus } from 'lucide-react'

export function PatientsPage() {
  const dispatch = useAppDispatch()
  const { patients, loading } = useAppSelector((s) => s.patientList)

  const [modalOpen, setModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [speciesFilter, setSpeciesFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('Active')

  useEffect(() => {
    dispatch(fetchPatients())
  }, [dispatch])

  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      // Search filter - matches patient name or owner name
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        if (
          !patient.name.toLowerCase().includes(q) &&
          !patient.owner_name.toLowerCase().includes(q)
        ) {
          return false
        }
      }
      // Species filter
      if (speciesFilter && patient.species !== speciesFilter) {
        return false
      }
      // Status filter
      if (statusFilter && patient.status !== statusFilter) {
        return false
      }
      return true
    })
  }, [patients, searchQuery, speciesFilter, statusFilter])

  if (loading) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="patients-page">
        <p className="text-[13px] text-text-muted">Loading patients...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="patients-page">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[16px] font-semibold text-text-primary">Patients</h1>
        <button
          data-testid="add-patient-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          <Plus size={14} />
          Add Patient
        </button>
      </div>

      {/* Search and filters */}
      <div className="mb-4">
        <PatientSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          speciesFilter={speciesFilter}
          onSpeciesFilterChange={setSpeciesFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <PatientListTable patients={filteredPatients} />
      </div>

      {/* Add Patient Modal */}
      <AddPatientModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
